import { type CookieAttributes } from '@/libs/cookies';
import jsCookie from '@/libs/cookies';

export type CookieOptions = {
  path?: string;
  expires?: number | Date;
  maxAge?: number;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
};

/**
 * Sets a cookie in the browser
 * @param name - Cookie name
 * @param value - Cookie value (will be serialized as JSON if it's an object)
 * @param options - Cookie options (path, maxAge, sameSite, etc.)
 */
export const setCookie = (
  name: string,
  value: string | Record<string, unknown>,
  options: CookieOptions = {},
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const stringValue = typeof value === 'string' ? value : JSON.stringify(value);

  const { maxAge, ...restOptions } = options;
  const cookieOptions: CookieAttributes = {
    path: '/',
    sameSite: 'lax',
    ...restOptions,
  };

  if (maxAge !== undefined) {
    const expiresDate = new Date();
    expiresDate.setTime(expiresDate.getTime() + maxAge * 1000);
    cookieOptions.expires = expiresDate;
  }

  jsCookie.set(name, stringValue, cookieOptions);
};

/**
 * Gets a cookie value
 * @param name - Cookie name
 * @returns Cookie value (deserialized if JSON) or null if it doesn't exist
 */
export const getCookie = <T = unknown>(name: string): T | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = jsCookie.get(name);

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return value as T;
  }
};

/**
 * Removes a cookie
 * @param name - Cookie name
 * @param options - Cookie options (path, domain, etc.)
 */
export const removeCookie = (
  name: string,
  options: Pick<CookieOptions, 'path' | 'domain'> = {},
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const defaultOptions = {
    path: '/',
    ...options,
  };

  jsCookie.remove(name, defaultOptions);
};

/**
 * Checks if a cookie exists
 * @param name - Cookie name
 * @returns true if the cookie exists, false otherwise
 */
export const hasCookie = (name: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return jsCookie.get(name) !== undefined;
};

/**
 * Gets all cookies as an object
 * @returns Object with all cookies
 */
export const getAllCookies = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }

  return jsCookie.get();
};
